import React, { createContext, useContext, useState, useEffect } from 'react'
import styled from 'styled-components'
import WalletModal from '../WalletModal'
import { useNotification } from '../NotificationSystem'
import { useDemo } from '../DemoProvider'

const WalletContext = createContext(null)

const WalletStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  ${props => props.$demo && `
    background: rgba(59, 130, 246, 0.1);
    cursor: default;

    &:hover {
      background: rgba(59, 130, 246, 0.1);
    }
  `}
`

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    if (props.$demo) return 'var(--color-primary)'
    return props.$connected ? 'var(--color-secondary)' : 'var(--color-text-secondary)'
  }};
`

const WalletButton = styled.button`
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;
  font-size: 0.875rem;

  &:hover {
    transform: translateY(-2px);
  }
`

const WalletAddress = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    background: var(--color-secondary);
    border-radius: 50%;
  }
`

export function WalletProvider({ children }) {
  const { addNotification } = useNotification()
  const { isDemoMode, enterDemoMode } = useDemo()
  const [wallet, setWallet] = useState({
    isConnected: false,
    publicKey: null,
    balance: null,
    error: null
  })
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    let notificationId = null;

    // Check if wallet was previously connected
    const checkWalletConnection = async () => {
      if (typeof window !== 'undefined' && window.solana) {
        try {
          if (window.solana.isConnected) {
            const publicKey = window.solana.publicKey.toString()
            const connection = new window.solana.Connection('https://api.mainnet-beta.solana.com')
            const balance = await connection.getBalance(window.solana.publicKey)
            
            setWallet({
              isConnected: true,
              publicKey,
              balance: balance / 1e9,
              error: null
            })

            addNotification({
              type: 'success',
              title: 'Wallet Connected',
              message: `Connected to ${formatAddress(publicKey)}`,
              duration: 3000
            })
          }
        } catch (error) {
          console.error('Error checking wallet connection:', error)
          addNotification({
            type: 'error',
            title: 'Connection Error',
            message: 'Failed to verify wallet connection.',
            duration: 5000
          })
        }
      }
    }

    if (!isDemoMode) {
      checkWalletConnection()

      // Listen for wallet connection changes
      if (typeof window !== 'undefined' && window.solana && typeof window.solana.on === 'function') {
        const handleConnect = () => {
          const publicKey = window.solana.publicKey.toString()
          setWallet(prev => ({
            ...prev,
            isConnected: true,
            publicKey,
            error: null
          }))

          if (notificationId) {
            notificationId = null
          }
        }

        const handleDisconnect = () => {
          setWallet({
            isConnected: false,
            publicKey: null,
            balance: null,
            error: null
          })
          addNotification({
            type: 'info',
            title: 'Wallet Disconnected',
            message: 'Your wallet has been disconnected.',
            duration: 3000
          })
        }

        const handleAccountChange = async (publicKey) => {
          if (publicKey) {
            const connection = new window.solana.Connection('https://api.mainnet-beta.solana.com')
            const balance = await connection.getBalance(publicKey)
            
            setWallet(prev => ({
              ...prev,
              publicKey: publicKey.toString(),
              balance: balance / 1e9
            }))

            addNotification({
              type: 'info',
              title: 'Account Changed',
              message: `Switched to ${formatAddress(publicKey.toString())}`,
              duration: 3000
            })
          }
        }

        window.solana.on('connect', handleConnect)
        window.solana.on('disconnect', handleDisconnect)
        window.solana.on('accountChanged', handleAccountChange)

        return () => {
          if (window.solana && typeof window.solana.removeListener === 'function') {
            window.solana.removeListener('connect', handleConnect)
            window.solana.removeListener('disconnect', handleDisconnect)
            window.solana.removeListener('accountChanged', handleAccountChange)
          }
        }
      } else {
        // Show a notification when Phantom is not installed
        addNotification({
          type: 'warning',
          title: 'Wallet Not Found',
          message: 'Install Phantom wallet or try Demo Mode to continue.',
          duration: 0,
          onClick: () => window.open('https://phantom.app/', '_blank')
        })
      }
    }
  }, [addNotification, isDemoMode])

  const connectWallet = async () => {
    try {
      if (typeof window === 'undefined' || !window.solana) {
        if (!isDemoMode) {
          setWallet(prev => ({ ...prev, error: 'Phantom wallet not installed' }))
          addNotification({
            type: 'warning',
            title: 'Wallet Not Found',
            message: 'Install Phantom wallet or try Demo Mode to continue.',
            duration: 5000
          })
        }
        return
      }

      const response = await window.solana.connect()
      
      // Get SOL balance
      const connection = new window.solana.Connection('https://api.mainnet-beta.solana.com')
      const balance = await connection.getBalance(response.publicKey)
      
      setWallet({
        isConnected: true,
        publicKey: response.publicKey.toString(),
        balance: balance / 1e9,
        error: null
      })

      addNotification({
        type: 'success',
        title: 'Wallet Connected',
        message: `Connected to ${formatAddress(response.publicKey.toString())}`,
        duration: 3000
      })
    } catch (error) {
      console.error('Error connecting wallet:', error)
      setWallet(prev => ({
        ...prev,
        error: error.message || 'Failed to connect wallet'
      }))
      addNotification({
        type: 'error',
        title: 'Connection Failed',
        message: error.message || 'Failed to connect wallet. Please try again.',
        duration: 5000
      })
    }
  }

  const disconnectWallet = async () => {
    try {
      if (typeof window !== 'undefined' && window.solana) {
        await window.solana.disconnect()
        setWallet({
          isConnected: false,
          publicKey: null,
          balance: null,
          error: null
        })
        addNotification({
          type: 'info',
          title: 'Wallet Disconnected',
          message: 'Your wallet has been disconnected.',
          duration: 3000
        })
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error)
      addNotification({
        type: 'error',
        title: 'Disconnect Failed',
        message: 'Failed to disconnect wallet. Please try again.',
        duration: 5000
      })
    }
  }

  const formatAddress = (address) => {
    if (!address) return ''
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }

  const WalletControl = () => (
    <>
      {wallet.isConnected ? (
        <WalletStatus onClick={disconnectWallet} title="Click to disconnect">
          <StatusDot $connected />
          <span>{formatAddress(wallet.publicKey)}</span>
          {wallet.balance !== null && (
            <span>({wallet.balance.toFixed(2)} SOL)</span>
          )}
        </WalletStatus>
      ) : isDemoMode ? (
        <WalletStatus $demo title="Demo Mode Active">
          <StatusDot $demo />
          <span>Demo Mode</span>
        </WalletStatus>
      ) : (
        <WalletButton onClick={() => setIsModalOpen(true)}>
          Connect Wallet
        </WalletButton>
      )}
      <WalletModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={connectWallet}
        onEnterDemo={() => {
          setIsModalOpen(false)
          enterDemoMode()
        }}
      />
    </>
  )

  return (
    <WalletContext.Provider 
      value={{ 
        wallet, 
        connectWallet, 
        disconnectWallet, 
        WalletControl,
        openModal: () => setIsModalOpen(true)
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export const useWallet = () => {
  const context = useContext(WalletContext)
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider')
  }
  return context
}
